import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import seatIcon from "../images/New folder (4)/bus/seat.png";
import steering from "../images/New folder (4)/bus/stearing wheel.png";
import "../css/seatbooking02.css";
import Contact from "../Navbar/Pages/Contact";
import RoomBooking from "../Navbar/Pages/roombooking";
import { BASE_URL } from "../../src/constants/constant";

const Seatbooking02 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { packageId, bustype, seatprice, packageprice } = location.state || {};

  const [seats, setSeats] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entryPoints, setEntryPoints] = useState([]);
  const [selectedEntryPoint, setSelectedEntryPoint] = useState("");
  const [adults, setAdults] = useState(0);
  const [children, setChildren] = useState(0);
  const [rooms, setRooms] = useState(0);
  const [totalGuests, setTotalGuests] = useState(0);

  const userId = sessionStorage.getItem("user_id");
  const pricePerSeat = seatprice;

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await fetch(`${BASE_URL}seatfetching.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bustype, packageId }),
        });

        const data = await response.json();
        if (data.success) {
          setSeats(
            data.seats.map((seat) => ({
              id: seat.id,
              seat_name: seat.seat_name,
              available: seat.available,
              payment_status: seat.payment_status || "available",
            }))
          );
        } else {
          setError(data.message || "Failed to fetch seats.");
        }
      } catch (err) {
        setError(err.message || "An error occurred while fetching seats.");
      } finally {
        setLoading(false);
      }
    };

    const fetchEntryPoints = async () => {
      try {
        const response = await fetch(`${BASE_URL}entrypointfetching.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ packageId }),
        });

        const data = await response.json();
        if (data.success) {
          setEntryPoints(data.entrypoints || []);
        } else {
          setError(data.message || "Failed to fetch entry points.");
        }
      } catch (err) {
        setError(
          err.message || "An error occurred while fetching entry points."
        );
      }
    };

    if (bustype && packageId) {
      fetchDetails();
      fetchEntryPoints();
    } else {
      setError("Invalid bus type or package ID");
      setLoading(false);
    }

    return () => {
      unlockSeats();
    };
  }, [bustype, packageId]);

  const toggleSeat = (seat) => {
    if (
      !seat.available ||
      seat.payment_status === "booked" ||
      seat.payment_status === "locked"
    )
      return;

    if (totalGuests <= 0) {
      alert("Please set total number of guests before selecting seats.");
      return;
    }

    const alreadySelected = selectedSeats.some((s) => s.id === seat.id);
    const totalSelected = selectedSeats.length + (alreadySelected ? 0 : 1);

    if (totalSelected > totalGuests) {
      alert(`You can only select up to ${totalGuests} seats.`);
      return;
    }

    const updatedSelectedSeats = alreadySelected
      ? selectedSeats.filter((s) => s.id !== seat.id)
      : [...selectedSeats, { id: seat.id, name: seat.seat_name }];

    setSelectedSeats(updatedSelectedSeats);
  };

  const lockSeats = async () => {
    const seatIds = selectedSeats.map((seat) => seat.id);
    try {
      const response = await fetch(`${BASE_URL}lock_seat.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          package_id: packageId,
          seat_ids: seatIds,
          bus_id: bustype,
          payment_status: "booked",
          balance_payment: "pending",
          entry_point: selectedEntryPoint,
          adults,
          children,
          rooms,
        }),
      });

      const data = await response.json();
      return data.success ? data.booking_id : false;
    } catch (error) {
      console.error("Error locking seats:", error);
      return false;
    }
  };

  const unlockSeats = async () => {
    if (selectedSeats.length === 0) return;

    const seatIds = selectedSeats.map((seat) => seat.id);

    try {
      const response = await fetch(`${BASE_URL}unlock_seat.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          seat_ids: seatIds,
          package_id: packageId,
          user_id: userId,
        }),
      });

      const data = await response.json();
      if (!data.success) {
        console.error("Unlock seats failed:", data.message);
      }
    } catch (err) {
      console.error("Unlock seats error:", err);
    }
  };

  const handleProceedToBilling = async () => {
    if (selectedSeats.length === 0) {
      alert("Please select at least one seat.");
      return;
    }
    if (!selectedEntryPoint) {
      alert("Please select an entry point.");
      return;
    }

    const bookingId = await lockSeats();
    if (!bookingId) {
      alert("Failed to lock seats. Try again.");
      return;
    }

    navigate("/billing", {
      state: {
        bookingId,
        selectedSeats,
        totalAmount: getTotalPrice(),
        packageId,
        bustype,
        selectedEntryPoint,
        adults,
        children,
        rooms,
      },
    });
  };

  const getTotalPrice = () => pricePerSeat * selectedSeats.length;

  const organizeSeats = (seats) => {
    const rows = [];
    const firstRow = seats.slice(0, 3);
    const remainingSeats = seats.slice(3);

    for (let i = 0; i < remainingSeats.length; i += 5) {
      rows.push({
        left: remainingSeats.slice(i, i + 2),
        right: remainingSeats.slice(i + 2, i + 5),
      });
    }

    return { firstRow, rows };
  };

  const { firstRow, rows } = organizeSeats(seats);

  if (loading) return <div>Loading seats...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      <Navbar />
      <NavLink to="/backbutton" className="back-button">
        ←
      </NavLink>

      <RoomBooking
        onApply={(guests, adults, children, rooms) => {
          setTotalGuests(guests);
          setAdults(adults);
          setChildren(children);
          setRooms(rooms);
        }}
      />

      <div className="seatbooking02-container">
        <div className="entry-point-container">
          <label htmlFor="entry-point">Choose Entry Point:</label>
          <select
            id="entry-point"
            className="entrypoint-select"
            value={selectedEntryPoint}
            onChange={(e) => setSelectedEntryPoint(e.target.value)}
          >
            <option value="" disabled>
              Select Entry Point
            </option>
            {entryPoints.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.entryplace}
              </option>
            ))}
          </select>
        </div>

        <h2>Select Your Seat</h2>

        <div className="seatbooking02-bus-layout">
          <div className="seatbooking02-drive">
            Driver{" "}
            <img
              src={steering}
              alt="Steering"
              className="seatbooking02-steering-icon"
            />
          </div>

          <div className="seatbooking02-seats-grid">
            <div className="seatbooking02-seat-row">
              <div className="seatbooking02-seat empty"></div>
              <div className="seatbooking02-seat empty"></div>
              <div className="seatbooking02-seat walking-space"></div>
              {firstRow.map((seat) => (
                <div
                  key={seat.id}
                  className={`seatbooking02-seat ${
                    seat.payment_status !== "available" ? "booked" : ""
                  } ${
                    selectedSeats.some((s) => s.id === seat.id)
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => toggleSeat(seat)}
                >
                  <img src={seatIcon} alt="Seat" width="30" />
                  <div className="seatbooking02-tooltip">
                    Seat {seat.seat_name}
                  </div>
                </div>
              ))}
            </div>

            {rows.map((row, i) => (
              <div key={i} className="seatbooking02-seat-row">
                {row.left.map((seat) => (
                  <div
                    key={seat.id}
                    className={`seatbooking02-seat ${
                      seat.payment_status !== "available" ? "booked" : ""
                    } ${
                      selectedSeats.some((s) => s.id === seat.id)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleSeat(seat)}
                  >
                    <img src={seatIcon} alt="Seat" width="30" />
                    <div className="seatbooking02-tooltip">
                      Seat {seat.seat_name}
                    </div>
                  </div>
                ))}
                <div className="seatbooking02-seat walking-space"></div>
                {row.right.map((seat) => (
                  <div
                    key={seat.id}
                    className={`seatbooking02-seat ${
                      seat.payment_status !== "available" ? "booked" : ""
                    } ${
                      selectedSeats.some((s) => s.id === seat.id)
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => toggleSeat(seat)}
                  >
                    <img src={seatIcon} alt="Seat" width="30" />
                    <div className="seatbooking02-tooltip">
                      Seat {seat.seat_name}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="total-price">
          <h3>Total Advance Amount: ₹{getTotalPrice()}</h3>

          <button className="billing-link" onClick={handleProceedToBilling}>
            Proceed to Billing
          </button>
        </div>
      </div>
      <Contact />
    </div>
  );
};

export default Seatbooking02;
